import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import './stepchart.css';

/**
 * Ступенчатый график по образцу статистики Telegram (экраны 8 и 14): шкала с круглым
 * шагом, ≤7 подписей дат, подсказка, «По часам / По дням», полоса-обзор всей истории
 * с окном (перетаскивание, края, щелчок мимо окна), метки серий, быстрый выбор.
 * Период — управляемый (range/onRange): на «Балансе» он общий для всей страницы.
 */

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const DAY_MS = 86_400_000;

/** «2026-10-28» → Date (полночь UTC — только для арифметики по дням). */
const d0 = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (s: string, n: number) => iso(new Date(d0(s).getTime() + n * DAY_MS));
const daysBetween = (a: string, b: string) =>
  Math.round((d0(b).getTime() - d0(a).getTime()) / DAY_MS);
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MONTHS[Number(s.slice(5, 7)) - 1]}`;
export const rangeText = (a: string, b: string) =>
  `${dayLabel(a)} — ${dayLabel(b)} ${b.slice(0, 4)}`;

/** Круглый шаг шкалы: 1/2/5 × 10^k, не больше 5 делений. */
function niceTicks(max: number, integer: boolean) {
  if (max <= 0.01) return [0, 1];
  const raw = max / 5;
  const p = 10 ** Math.floor(Math.log10(raw));
  const stepV = [1, 2, 2.5, 5, 10].map((m) => m * p).find((s) => max / s <= 5) ?? 10 * p;
  const step = integer ? Math.max(1, stepV) : Math.max(0.01, stepV);
  const top = Math.ceil(max / step - 1e-9) * step;
  const out: number[] = [];
  for (let i = 0; i * step <= top + 1e-9; i += 1)
    out.push(integer ? Math.round(i * step) : Math.round(i * step * 100) / 100);
  return out;
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(200, Math.floor(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

function stepPath(vals: number[], x: (i: number) => number, y: (v: number) => number, end: number) {
  if (!vals.length) return '';
  let d = `M${x(0)},${y(vals[0])}`;
  for (let i = 1; i < vals.length; i += 1) d += `H${x(i)}V${y(vals[i])}`;
  return `${d}H${end}`;
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
}

export type ChartPoint = { t: string } & Record<string, number | string>;

interface Props {
  title: string;
  series: ChartSeries[];
  points: ChartPoint[];
  overview: ChartPoint[];
  overviewKey: string;
  step: 'day' | 'hour';
  onStep: (s: 'day' | 'hour') => void;
  range: { from: string; to: string };
  onRange: (r: { from: string; to: string }) => void;
  today: string;
  fetching?: boolean;
  fmt: (v: number) => string;
  headExtra?: ReactNode;
  emptyText?: string;
  /** Целые значения (штуки) — шаг шкалы не меньше 1; для рублей — false. */
  integer?: boolean;
}

export function StepChart({
  title,
  series: SERIES,
  points: rawPoints,
  overview: rawOverview,
  overviewKey,
  step,
  onStep,
  range: r,
  onRange: setRange,
  today,
  fetching: isFetching,
  fmt,
  headExtra,
  emptyText = 'За этот период данных нет',
  integer = true,
}: Props) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const points = useMemo(
    () =>
      rawPoints.map((p) => {
        const o: Record<string, number | string> = { t: p.t };
        for (const s of SERIES) o[s.key] = Number(p[s.key] ?? 0);
        return o as ChartPoint;
      }),
    [rawPoints, SERIES],
  );
  const overview = useMemo(
    () => rawOverview.map((p) => ({ t: p.t, v: Number(p[overviewKey] ?? 0) })),
    [rawOverview, overviewKey],
  );
  const firstDay = overview[0]?.t ?? addDays(today, -89);
  const val = (p: ChartPoint, k: string) => Number(p[k] ?? 0);

  const [mainRef, W] = useWidth<HTMLDivElement>();
  const H = 220;
  const padL = 36;
  const padB = 22;
  const plotW = W - padL;
  const plotH = H - padB - 8;
  const visible = SERIES.filter((s) => !hidden.has(s.key));
  const max = Math.max(0.0001, ...points.flatMap((p) => visible.map((s) => val(p, s.key))));
  const ticks = niceTicks(max, integer);
  const top = ticks[ticks.length - 1] || 1;
  const n = Math.max(1, points.length);
  const x = (i: number) => padL + (i * plotW) / n;
  const y = (v: number) => 8 + plotH - (v / top) * plotH;
  const labelIdx = useMemo(() => {
    const cnt = Math.min(7, points.length);
    if (!cnt) return [];
    const out = new Set<number>();
    for (let k = 0; k < cnt; k += 1)
      out.add(Math.round((k * (points.length - 1)) / Math.max(1, cnt - 1)));
    return [...out];
  }, [points.length]);
  const fmtX = (t: string) =>
    step === 'hour'
      ? t.slice(11, 13) === '00'
        ? dayLabel(t)
        : `${t.slice(11, 13)}:00`
      : dayLabel(t);
  const fmtTip = (t: string) =>
    step === 'hour'
      ? `${dayLabel(t)}, ${t.slice(11, 13)}:00–${t.slice(11, 13)}:59`
      : `${dayLabel(t)} ${t.slice(0, 4)}`;

  const [hover, setHover] = useState<number | null>(null);
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - padL;
    if (px < 0 || !points.length) return setHover(null);
    setHover(Math.min(points.length - 1, Math.max(0, Math.floor((px / plotW) * n))));
  };

  // ── Полоса-обзор ──
  const [ovRef, OW] = useWidth<HTMLDivElement>();
  const OH = 44;
  const ovN = Math.max(1, overview.length);
  const ovX = (i: number) => (i * OW) / ovN;
  const ovMax = Math.max(0.0001, ...overview.map((p) => p.v));
  const ovY = (v: number) => 4 + (OH - 8) - (v / ovMax) * (OH - 8);
  const idxOf = (day: string) => Math.max(0, Math.min(ovN, daysBetween(firstDay, day)));
  const [drag, setDragState] = useState<{ from: string; to: string } | null>(null);
  const dragRef = useRef<{ from: string; to: string } | null>(null);
  const setDrag = (v: { from: string; to: string } | null) => {
    dragRef.current = v;
    setDragState(v);
  };
  const shown = drag ?? r;
  const wx0 = ovX(idxOf(shown.from));
  const wx1 = ovX(idxOf(shown.to) + 1);
  const maxSpan = step === 'hour' ? 7 : 3660;

  const dayAt = (clientX: number, el: Element) => {
    const rect = el.getBoundingClientRect();
    const i = Math.floor(((clientX - rect.left) / rect.width) * ovN);
    return addDays(firstDay, Math.max(0, Math.min(ovN - 1, i)));
  };
  const clamp = (from: string, to: string) => {
    let f = from < firstDay ? firstDay : from;
    let t = to > today ? today : to;
    if (t < f) [f, t] = [t, f];
    if (daysBetween(f, t) + 1 > maxSpan) f = addDays(t, -(maxSpan - 1));
    return { from: f, to: t };
  };

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const start = { from: shown.from, to: shown.to };
    const span = daysBetween(start.from, start.to);
    const startDay = dayAt(e.clientX, el);
    let mode: 'move' | 'l' | 'r';
    if (Math.abs(px - wx0) < 8) mode = 'l';
    else if (Math.abs(px - wx1) < 8) mode = 'r';
    else if (px > wx0 && px < wx1) mode = 'move';
    else {
      // Щелчок мимо окна — переносим окно туда.
      const c = dayAt(e.clientX, el);
      const f = addDays(c, -Math.floor(span / 2));
      const next = clamp(f, addDays(f, span));
      setRange(next);
      return;
    }
    const move = (ev: PointerEvent) => {
      const day = dayAt(ev.clientX, el);
      const shift = daysBetween(startDay, day);
      if (mode === 'move') {
        let f = addDays(start.from, shift);
        let t = addDays(start.to, shift);
        if (f < firstDay) {
          f = firstDay;
          t = addDays(f, span);
        }
        if (t > today) {
          t = today;
          f = addDays(t, -span);
        }
        setDrag(clamp(f, t));
      } else if (mode === 'l') setDrag(clamp(day < start.to ? day : start.to, start.to));
      else setDrag(clamp(start.from, day > start.from ? day : start.from));
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      if (dragRef.current) setRange(dragRef.current);
      setDrag(null);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };

  // С клавиатуры: ←/→ сдвигают окно на день, Home/End — к началу/концу.
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const span = daysBetween(r.from, r.to);
    let f: string | null = null;
    if (e.key === 'ArrowLeft') f = addDays(r.from, -1);
    else if (e.key === 'ArrowRight') f = addDays(r.from, 1);
    else if (e.key === 'Home') f = firstDay;
    else if (e.key === 'End') f = addDays(today, -span);
    if (f == null) return;
    e.preventDefault();
    if (f < firstDay) f = firstDay;
    if (addDays(f, span) > today) f = addDays(today, -span);
    if (f !== r.from) setRange({ from: f, to: addDays(f, span) });
  };

  const quick = step === 'hour' ? [1, 3, 7] : [7, 30, 90];
  const curSpan = daysBetween(r.from, r.to) + 1;

  const hv = hover != null ? points[hover] : null;

  return (
    <div className="dl-chart">
      <div className="dl-chart-head">
        <span className="v2-h2">{title}</span>
        <span style={{ flex: 1 }} />
        <div className="dl-seg" role="radiogroup" aria-label="Шаг графика">
          {(
            [
              ['hour', 'По часам'],
              ['day', 'По дням'],
            ] as const
          ).map(([v, t]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={step === v}
              className={step === v ? 'is-on' : undefined}
              onClick={() => onStep(v)}
            >
              {t}
            </button>
          ))}
        </div>
        <b className="dl-range">{rangeText(shown.from, shown.to)}</b>
        {headExtra}
      </div>

      <div ref={mainRef} className="dl-plot" style={{ opacity: isFetching ? 0.6 : 1 }}>
        <svg
          width={W}
          height={H}
          role="img"
          aria-label={`График: ${visible.map((s) => s.label).join(', ')}`}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={padL} x2={W} y1={y(t)} y2={y(t)} stroke="#F3F4F6" />
              <text x={padL - 8} y={y(t) + 4} textAnchor="end" className="dl-ax">
                {fmt(t)}
              </text>
            </g>
          ))}
          {labelIdx.map((i) => (
            <text
              key={i}
              x={Math.min(W - 24, Math.max(padL + 18, x(i) + plotW / n / 2))}
              y={H - 4}
              textAnchor="middle"
              className="dl-ax"
            >
              {points[i] ? fmtX(String(points[i].t)) : ''}
            </text>
          ))}
          {[...visible].reverse().map((s) => (
            <path
              key={s.key}
              d={stepPath(
                points.map((p) => val(p, s.key)),
                x,
                y,
                W,
              )}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
            />
          ))}
          {hv && hover != null && (
            <line
              x1={x(hover) + plotW / n / 2}
              x2={x(hover) + plotW / n / 2}
              y1={8}
              y2={8 + plotH}
              stroke="#9CA3AF"
              strokeDasharray="3 3"
            />
          )}
        </svg>
        {hv && hover != null && (
          <div
            className="dl-tip"
            style={{
              left: Math.min(W - 170, Math.max(0, x(hover) + plotW / n / 2 + 10)),
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{fmtTip(String(hv.t))}</div>
            {visible.map((s) => (
              <div key={s.key} className="dl-tip-row">
                <i style={{ background: s.color }} />
                {s.label}
                <b>{fmt(val(hv, s.key))}</b>
              </div>
            ))}
          </div>
        )}
        {!isFetching &&
          points.length > 0 &&
          points.every((p) => SERIES.every((s) => val(p, s.key) === 0)) && (
            <div className="dl-empty">{emptyText}</div>
          )}
      </div>

      <div
        ref={ovRef}
        className="dl-overview"
        onPointerDown={onDown}
        onKeyDown={onKey}
        role="slider"
        aria-label="Период графика: перетащите окно или сдвигайте стрелками"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, ovN - 1)}
        aria-valuenow={Math.max(0, idxOf(shown.from))}
        aria-valuetext={rangeText(shown.from, shown.to)}
        tabIndex={0}
      >
        <svg width={OW} height={OH} aria-hidden="true">
          <path
            d={stepPath(
              overview.map((p) => p.v),
              ovX,
              ovY,
              OW,
            )}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth={1}
          />
          <rect x={0} y={0} width={Math.max(0, wx0)} height={OH} fill="rgba(249,250,251,0.75)" />
          <rect
            x={wx1}
            y={0}
            width={Math.max(0, OW - wx1)}
            height={OH}
            fill="rgba(249,250,251,0.75)"
          />
          <rect
            x={wx0}
            y={1}
            width={Math.max(4, wx1 - wx0)}
            height={OH - 2}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth={2}
            rx={4}
          />
          <rect x={wx0 - 2} y={OH / 2 - 9} width={4} height={18} rx={2} fill="#6B7280" />
          <rect x={wx1 - 2} y={OH / 2 - 9} width={4} height={18} rx={2} fill="#6B7280" />
        </svg>
      </div>

      <div className="dl-chart-foot">
        {SERIES.map((s) => {
          const on = !hidden.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={on}
              className="dl-series"
              style={
                on
                  ? { background: s.color, borderColor: s.color, color: '#fff' }
                  : { color: s.color, borderColor: s.color }
              }
              onClick={() =>
                setHidden((h) => {
                  const next = new Set(h);
                  if (next.has(s.key)) next.delete(s.key);
                  else if (SERIES.length - next.size > 1) next.add(s.key);
                  return next;
                })
              }
            >
              {on ? '✓ ' : ''}
              {s.label}
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        {quick.map((q) => (
          <button
            key={q}
            type="button"
            className={curSpan === q ? 'dl-quick is-on' : 'dl-quick'}
            onClick={() => setRange(clamp(addDays(today, -(q - 1)), today))}
          >
            {q} {step === 'hour' ? (q === 1 ? 'день' : q < 5 ? 'дня' : 'дней') : 'дней'}
          </button>
        ))}
      </div>
    </div>
  );
}
